import Layout from "../components/Layout";
import { Alert, Box, Button, Grid2, Paper, Snackbar, TextField, Typography } from "@mui/material";
import ConfirmationModal from "../components/ConfirmationModal";
import NewQuestionForm from "../components/NewQuestionForm";
import ReorderModal from "../components/ReorderModal";
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { createLevelForAdmin, createQuestionsForLevel, getUnitDetails } from "../service/service";

function AddLevel() {
  const { courseId, unitId } = useParams();
  const [courseData, setCourseData] = useState(null);
  const [unitData, setUnitData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [newQuestionForms, setNewQuestionForms] = useState([]);
  const [questionFormData, setQuestionFormData] = useState({});
  const [levelName, setLevelName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [levelNameError, setLevelNameError] = useState("");
  const newQuestionFormCounter = useRef(0);
  const questionFormRefs = useRef({});

  const questionEntries = useMemo(
    () =>
      newQuestionForms
        .map((form) => {
          const entry = questionFormData[form.id];
          if (!entry?.payload) {
            return null;
          }
          return { ...entry, formId: form.id };
        })
        .filter(Boolean),
    [newQuestionForms, questionFormData]
  );

  const questionPayloadList = useMemo(
    () =>
      questionEntries
        .map(({ payload }) => payload)
        .filter((payload) => payload?.question?.trim()),
    [questionEntries]
  );

  const reorderableQuestions = useMemo(
    () =>
      newQuestionForms.map((form, index) => {
        const entry = questionFormData[form.id];
        const questionText = entry?.payload?.question?.trim() || `Question ${index + 1}`;
        return { _id: form.id, question: questionText };
      }),
    [newQuestionForms, questionFormData]
  );

  const loadUnitDetails = useCallback(async () => {
    try {
      const { data } = await getUnitDetails(courseId, unitId);
      setUnitData(data.unit);
      setCourseData({
        courseId: data._id,
        name: data.name,
        isDeleted: data.isDeleted,
        isDraft: data.isDraft,
      });
      setLevels(data.levels);
      setNewQuestionForms([]);
      newQuestionFormCounter.current = 0;
      setQuestionFormData({});
      questionFormRefs.current = {};
      setLevelName("");
      setSnackbar({
        open: false,
        message: "",
        severity: "success",
      });
      setLevelNameError("");
    } catch (error) {
      console.error('Failed to fetch unit details:', error);
    }
  }, [courseId, unitId]);

  useEffect(() => {
    loadUnitDetails();
  }, [loadUnitDetails]);

  const handleAddQuestionForm = () => {
    newQuestionFormCounter.current += 1;
    const tempId = `new-form-${Date.now()}-${newQuestionFormCounter.current}`;
    setNewQuestionForms((prev) => [...prev, { id: tempId }]);
    setSnackbar({
      open: false,
      message: "",
      severity: "success",
    });
  };

  const handleRemoveQuestionForm = (formId) => {
    setNewQuestionForms((prev) => prev.filter((form) => form.id !== formId));
    delete questionFormRefs.current[formId];
    setQuestionFormData((prev) => {
      if (!prev[formId]) {
        return prev;
      }
      const next = { ...prev };
      delete next[formId];
      return next;
    });
  };

  const getQuestionFormRef = (formId) => {
    if (!questionFormRefs.current[formId]) {
      questionFormRefs.current[formId] = createRef();
    }
    return questionFormRefs.current[formId];
  };

  const handleReorderQuestionForms = async (reorderedQuestions) => {
    const orderMap = new Map(reorderedQuestions.map((item) => [item._id, item.order]));
    setNewQuestionForms((prev) =>
      [...prev].sort(
        (a, b) =>
          (orderMap.get(a.id) || Number.MAX_SAFE_INTEGER) -
          (orderMap.get(b.id) || Number.MAX_SAFE_INTEGER)
      )
    );

    setSnackbar({
      open: true,
      message: "Questions reordered successfully.",
      severity: "success",
    });
  };

  const handleQuestionFormChange = (formId, data) => {
    setQuestionFormData((prev) => {
      if (!data?.payload?.question?.trim()) {
        if (!prev[formId]) {
          return prev;
        }
        const next = { ...prev };
        delete next[formId];
        return next;
      }
      return { ...prev, [formId]: data };
    });
  };

  const handleSaveNewLevel = async () => {
    setSnackbar({ open: false, message: "", severity: "success" });
    const trimmedLevelName = levelName.trim();
    if (!trimmedLevelName) {
      setLevelNameError("Level name is required.");
      setSnackbar({ open: true, severity: "error", message: "Provide a name for the level." });
      return;
    }

    // Validate question forms
    const levelLabel = `Level ${(levels?.length || 0) + 1}`;
    const validationErrors = [];
    const validatedQuestions = [];

    newQuestionForms.forEach((form, index) => {
      const entry = questionFormData[form.id];
      const questionNumber = index + 1;

      // Require question name
      if (!entry?.payload?.question?.trim()) {
        validationErrors.push(
          `Fill or remove ${levelLabel}, Question ${questionNumber}.`,
        );
        return;
      }

      const formRef = questionFormRefs.current[form.id];
      const validatedData = formRef?.current?.validateAndBuild?.();
      if (!validatedData) {
        validationErrors.push(
          `Fix validation issues in ${levelLabel}, Question ${questionNumber}.`,
        );
        return;
      }

      validatedQuestions.push(validatedData);
    });

    if (validationErrors.length) {
      setSnackbar({ severity: "error", message: validationErrors[0], open: true });
      return;
    }

    if (!validatedQuestions.length) {
      setSnackbar({
        severity: "error",
        message: "Add at least one valid question before saving.",
        open: true,
      });
      return;
    }

    setLevelNameError("");
    setIsSaving(true);
    try {
      const levelPayload = {
        name: trimmedLevelName,
        order: (levels?.length || 0) + 1,
        isDeleted: false,
        parentUnitId: unitId,
        parentCourseId: courseId,
      };

      const levelResponse = await createLevelForAdmin(levelPayload);
      const createdLevel = levelResponse?.data ?? levelResponse;
      const createdLevelId = createdLevel?._id;

      if (!createdLevelId) {
        throw new Error("Level creation succeeded but no level ID was returned.");
      }

      const questionsToCreate = validatedQuestions.map((validatedData, index) => ({
        payload: {
          ...validatedData.payload,
          parentLevelId: createdLevelId,
          parentUnitId: unitId,
          parentCourseId: courseId,
          order: index + 1,
        },
        attachments: validatedData.attachments,
      }));

      if (questionsToCreate.length) {
        const bulkResponse = await createQuestionsForLevel(questionsToCreate, {
          notificationType: "new-level",
          notificationMessage: "new level just dropped. Tap to jump in now.",
        });

        if (!bulkResponse?.success) {
          const errorMessage =
            bulkResponse?.error?.message ||
            bulkResponse?.message ||
            "Failed to save questions for the level.";
          throw new Error(errorMessage);
        }

        await loadUnitDetails(); // Reload data first
        setSnackbar({
          severity: "success",
          message: `${levelResponse?.message} • ${bulkResponse?.message}` || "New level and questions saved successfully!",
          open: true,
        }); // Then show success message
      }
    } catch (error) {
      console.error("Failed to save new level:", error);
      const errorMessage =
        error?.response?.data?.err ||
        error?.message ||
        "Failed to save the new level. Please try again.";
      setSnackbar({ severity: "error", message: errorMessage, open: true });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          height: "100%",
          width: "100%",
          backgroundColor: "background.default",
          p: 4,
        }}
      >
        <Paper elevation={0} sx={{ padding: "2rem", width: "100%" }}>
          <Typography variant="h5" gutterBottom>
            Edit Unit: {unitData?.name}
          </Typography>
          <Typography variant="subtitle1">Level Order: {levels?.length + 1}</Typography>

          <Paper
            sx={{ mt: 3, mb: 3, padding: "24px", border: "1px solid silver" }}
          >
            <Grid2 container sx={{ padding: "24px" }}>
              <Grid2 size={12} >
                <Typography sx={{ mt: 2, mb: 2 }} variant="h6">Level</Typography>
              </Grid2>
              <Grid2 size={12} gap={5} sx={{ display: "flex" }}>
                <TextField
                  label="Level Name"
                  variant="outlined"
                  fullWidth
                  value={levelName}
                  onChange={(event) => {
                    setLevelName(event.target.value);
                    if (levelNameError) {
                      setLevelNameError("");
                    }
                  }}
                  error={Boolean(levelNameError)}
                  helperText={levelNameError || undefined}
                />
              </Grid2>

              <Grid2 size={12}>
                {newQuestionForms.map((form, index) => (
                  <NewQuestionForm
                    key={form.id}
                    ref={getQuestionFormRef(form.id)}
                    courseData={courseData}
                    unitId={unitId}
                    levelOrder={levels.length + 1}
                    nextOrder={index + 1}
                    displayConfirmButton={false}
                    secondaryBtnText="Remove Question"
                    onChange={(data) => handleQuestionFormChange(form.id, data)}
                    onCancel={() => handleRemoveQuestionForm(form.id)}
                  />
                ))}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, gap: 2 }}>
                  <ReorderModal
                    title="Reorder new questions"
                    questions={reorderableQuestions}
                    confirmLabel="Apply Order"
                    cancelLabel="Cancel"
                    onConfirm={handleReorderQuestionForms}
                    isDisabled={reorderableQuestions.length < 2 || isSaving}
                  />
                  <Button variant="outlined" color="info" onClick={handleAddQuestionForm}>
                    Add Question Form
                  </Button>
                </Box>
              </Grid2>
            </Grid2>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ConfirmationModal
              title="Confirm new level"
              description={`Level name: ${levelName || "(unnamed)"}. Questions to add: ${questionPayloadList.length}`}
              helperText="This will create the level and notify eligible learners."
              buttonLabel={isSaving ? "Saving..." : "Save New Level"}
              loadingLabel="Saving..."
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              disabled={isSaving}
              loading={isSaving}
              renderTrigger={({ openModal, disabled }) => (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                  onClick={openModal}
                >
                  {isSaving ? "Saving Level..." : "Save New Level"}
                </Button>
              )}
              onConfirm={handleSaveNewLevel}
            />
          </Box>

          <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 2 }}>
            Number question form payloads: {questionPayloadList.length}
          </Typography>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}

export default AddLevel;
